import { ctaFeatures, gisFeatures, carbonFeatures } from "@/mocks/landing";

const CTA_IMAGE =
  "https://readdy.ai/api/search-image?query=Close%20up%20of%20large%20mature%20tree%20trunk%20with%20botanist%20measuring%20tape%2C%20urban%20park%20setting%2C%20warm%20sunlight%20filtering%20through%20leaves%2C%20professional%20environmental%20survey%2C%20editorial%20photography%20with%20soft%20bokeh&width=800&height=600&seq=floraiq-feat-cta-02&orientation=landscape";
const GIS_IMAGE =
  "https://readdy.ai/api/search-image?query=Aerial%20satellite%20view%20of%20green%20urban%20neighborhood%20with%20streets%20and%20parks%2C%20top-down%20perspective%2C%20lush%20tree%20canopy%2C%20clean%20modern%20mapping%20aesthetic%2C%20bright%20daylight%2C%20editorial%20photography&width=800&height=600&seq=floraiq-feat-gis-02&orientation=landscape";
const CARBON_IMAGE =
  "https://readdy.ai/api/search-image?query=Dense%20tropical%20forest%20canopy%20from%20above%20with%20sunlight%20breaking%20through%2C%20rich%20green%20layers%2C%20carbon%20sequestration%20concept%2C%20clean%20nature%20photography%2C%20vibrant%20greens%2C%20editorial%20style&width=800&height=600&seq=floraiq-feat-carbon-02&orientation=landscape";

function FeatureBlock({
  id,
  label,
  title,
  subtitle,
  features,
  accent,
  imageUrl,
  reverse,
}: {
  id: string;
  label: string;
  title: string;
  subtitle: string;
  features: string[];
  accent: "primary" | "accent";
  imageUrl: string;
  reverse?: boolean;
}) {
  const accentBg = accent === "primary" ? "bg-primary-50" : "bg-accent-50";
  const accentText = accent === "primary" ? "text-primary-600" : "text-accent-600";
  const accentBorder = accent === "primary" ? "border-primary-200" : "border-accent-200";
  const accentLabel = accent === "primary" ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-700";

  return (
    <div id={id} className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className={`flex flex-col ${reverse ? "lg:flex-row-reverse" : "lg:flex-row"} gap-10 md:gap-14 lg:gap-20 items-center`}>
          {/* Text */}
          <div className="flex-1 w-full">
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${accentLabel} mb-4`}>
              {label}
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {title}
            </h2>
            <p className="text-base md:text-lg text-foreground-600 mb-7 md:mb-10 leading-relaxed max-w-xl">
              {subtitle}
            </p>
            <ul className="space-y-3.5">
              {features.map((feat) => (
                <li key={feat} className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full ${accentBg} flex items-center justify-center shrink-0 mt-0.5`}>
                    <i className={`ri-check-line ${accentText} text-xs w-full h-full flex items-center justify-center`} />
                  </div>
                  <span className="text-sm md:text-[15px] text-foreground-700">{feat}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Visual with image background */}
          <div className="flex-1 w-full">
            {id === "cta-arboreo" && (
              <div className="relative rounded-2xl overflow-hidden border border-background-200/70 shadow-sm">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${imageUrl})` }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
                <div className="relative p-5 md:p-7">
                  <div className={`bg-white/95 backdrop-blur-sm rounded-xl border ${accentBorder} p-4 md:p-5`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-[11px] text-foreground-500 uppercase tracking-wider mb-0.5">Ficha Cadastral Arbórea</div>
                        <div className="text-lg font-bold text-foreground-950">CTA-2024-001847</div>
                      </div>
                      <div className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                        Ativo
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2.5 mb-3.5">
                      {[
                        { label: "Espécie", value: "Caesalpinia echinata" },
                        { label: "Porte", value: "Grande" },
                        { label: "DAP", value: "42 cm" },
                        { label: "Altura", value: "14,5 m" },
                      ].map((item) => (
                        <div key={item.label} className="bg-background-50 rounded-lg p-2.5">
                          <div className="text-[11px] text-foreground-500 mb-0.5">{item.label}</div>
                          <div className="text-sm font-semibold text-foreground-800">{item.value}</div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-background-50 rounded-lg p-3 mb-3">
                      <div className="flex justify-between text-xs mb-1.5">
                        <span className="text-foreground-600">Risco avaliado</span>
                        <span className="font-medium text-foreground-800">Baixo</span>
                      </div>
                      <div className="w-full bg-background-200 rounded-full h-1.5">
                        <div className="bg-primary-500 h-1.5 rounded-full" style={{ width: "28%" }} />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-background-100" />
                      <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-background-100" />
                      <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-background-100" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {id === "ambiental" && (
              <div className="relative rounded-2xl overflow-hidden border border-background-200/70 shadow-sm aspect-[4/3]">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${imageUrl})` }} />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />
                <div className="relative h-full p-5 md:p-7 flex flex-col justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                      <i className="ri-map-pin-line text-white text-sm w-full h-full flex items-center justify-center" />
                    </div>
                    <span className="text-sm font-semibold text-white">Mapa Arbóreo — Goiânia</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {["Risco", "Espécie", "Bairro", "Projeto"].map((f) => (
                      <span key={f} className="px-2.5 py-1 bg-white/20 backdrop-blur rounded-md text-xs text-white border border-white/20">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="absolute top-3 right-3 bg-white/90 backdrop-blur rounded-lg px-3 py-1.5 text-xs font-medium text-foreground-700">
                  12.486 árvores
                </div>
              </div>
            )}

            {id === "carbono" && (
              <div className="relative rounded-2xl overflow-hidden border border-background-200/70 shadow-sm">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${imageUrl})` }} />
                <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/20 to-transparent" />
                <div className="relative p-5 md:p-7">
                  <div className="text-sm font-semibold text-white mb-5">Indicadores de Carbono</div>
                  <div className="space-y-4 mb-5">
                    {[
                      { label: "CO₂e sequestrado", value: "1.847 ton", percent: 72, color: "bg-primary-500" },
                      { label: "Biomassa arbórea", value: "856 ton", percent: 58, color: "bg-accent-500" },
                      { label: "Áreas verdes mapeadas", value: "64 unidades", percent: 85, color: "bg-primary-500" },
                    ].map((item) => (
                      <div key={item.label}>
                        <div className="flex justify-between text-xs mb-1.5">
                          <span className="text-white/80">{item.label}</span>
                          <span className="font-medium text-white">{item.value}</span>
                        </div>
                        <div className="w-full bg-white/20 rounded-full h-2">
                          <div className={`${item.color} h-2 rounded-full`} style={{ width: `${item.percent}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-2.5">
                    <div className="bg-white/15 backdrop-blur rounded-lg p-3 text-center border border-white/10">
                      <div className="text-lg font-bold text-white">421</div>
                      <div className="text-[10px] text-white/70">Replantios</div>
                    </div>
                    <div className="bg-white/15 backdrop-blur rounded-lg p-3 text-center border border-white/10">
                      <div className="text-lg font-bold text-white">18</div>
                      <div className="text-[10px] text-white/70">Projetos</div>
                    </div>
                    <div className="bg-white/15 backdrop-blur rounded-lg p-3 text-center border border-white/10">
                      <div className="text-lg font-bold text-white">87</div>
                      <div className="text-[10px] text-white/70">Compensações</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FeaturesSection() {
  return (
    <div>
      <FeatureBlock
        id="cta-arboreo"
        label="Inventário Arbóreo"
        title="Do inventário à gestão permanente da arborização urbana"
        subtitle="O FloraIQ permite cadastrar, monitorar e gerenciar cada árvore da cidade com dados técnicos, georreferenciamento e histórico completo."
        features={ctaFeatures}
        accent="primary"
        imageUrl={CTA_IMAGE}
      />

      <div className="bg-background-100">
        <FeatureBlock
          id="ambiental"
          label="GIS Ambiental"
          title="Visualize o patrimônio verde no território"
          subtitle="Mapa interativo com camadas temáticas, filtros inteligentes e exportação GeoJSON para integração com sistemas GIS municipais."
          features={gisFeatures}
          accent="accent"
          imageUrl={GIS_IMAGE}
          reverse
        />
      </div>

      <FeatureBlock
        id="carbono"
        label="Carbono Urbano"
        title="Dados ambientais que apoiam sustentabilidade, ESG e planejamento urbano"
        subtitle="Estimativas de carbono sequestrado, biomassa arbórea, indicadores de áreas verdes e monitoramento de compensações ambientais."
        features={carbonFeatures}
        accent="primary"
        imageUrl={CARBON_IMAGE}
      />
    </div>
  );
}